import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  type MarkConsentRequest,
  PatientConsentStatus,
  createPatientConsentRecord,
} from '@repo/videa-notes';

import configurations from '@/config';
import { oktaAxiosInstance } from '@/utils/api';

/**
 * Safely converts unifiedAppointmentId to a number or undefined.
 * Returns undefined if the value is undefined, null, empty string, or NaN.
 */
const safeUnifiedAppointmentId = (
  unifiedAppointmentId: string | undefined,
): number | undefined => {
  if (!unifiedAppointmentId || unifiedAppointmentId.trim() === '') {
    return undefined;
  }

  const num = Number(unifiedAppointmentId);
  return isNaN(num) ? undefined : num;
};

interface PatientInfo {
  firstName?: string;
  lastName?: string;
  dob?: string;
}

interface UsePatientConsentProps {
  patient: PatientInfo | undefined;
  patientNameForConsentRecord: string;
  appointmentStartsAt: string | undefined;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  currentOrganizationId: string;
  currentPracticeId: string;
  username: string;
  enableVoiceNotesConsent: boolean;
  /** Called when recording starts with patient consent. Receives the consent record ID. */
  onStartRecordingSuccess?: (consentRecordId: string | undefined) => void;
  /** Called when recording starts as provider-only. Receives the consent record ID. */
  onStartProviderOnlyRecordingSuccess?: (
    consentRecordId: string | undefined,
  ) => void;
  onPatientConsentDeniedSuccess?: () => void;
}

interface UsePatientConsentReturn {
  patientConsentRecordId: string | undefined;
  handleStartRecording: () => Promise<void>;
  handleStartProviderOnlyRecording: () => Promise<void>;
  handlePatientConsentDenied: () => Promise<void>;
}

export const usePatientConsent = ({
  patient,
  patientNameForConsentRecord,
  appointmentStartsAt,
  unifiedAppointmentId,
  standalonePatientVisitId,
  currentOrganizationId,
  currentPracticeId,
  username,
  enableVoiceNotesConsent,
  onStartRecordingSuccess,
  onStartProviderOnlyRecordingSuccess,
  onPatientConsentDeniedSuccess,
}: UsePatientConsentProps): UsePatientConsentReturn => {
  const [patientConsentRecordId, setPatientConsentRecordId] = useState<
    string | undefined
  >(undefined);

  // Upload dependencies with axios instance for proper token refresh
  const uploadDeps = useMemo(
    () => ({
      apiUrl: configurations.API_URL,
      axiosInstance: oktaAxiosInstance,
    }),
    [],
  );

  /**
   * Handle recording start when patient consent is given (YES)
   */
  const handleStartRecording = useCallback(async () => {
    try {
      let consentId: string | undefined;
      if (enableVoiceNotesConsent) {
        const consentRequest: MarkConsentRequest = {
          consentStatus: PatientConsentStatus.YES,
          isGivenByPatient: true,
          patientFullName: patientNameForConsentRecord,
          patientAppointmentAt: appointmentStartsAt ?? '',
          recordedBy: username,
          patientDob: patient?.dob,
        };

        const response = await createPatientConsentRecord({
          consentRequest,
          unifiedAppointmentId: safeUnifiedAppointmentId(unifiedAppointmentId),
          standalonePatientVisitId: standalonePatientVisitId,
          currentOrganizationId,
          currentPracticeId,
          deps: uploadDeps,
        });
        consentId = response.id;
        setPatientConsentRecordId(consentId);
      }
      // Pass the consent record ID directly to avoid React state timing issues
      onStartRecordingSuccess?.(consentId);
    } catch (error) {
      console.error(error);
    }
  }, [
    enableVoiceNotesConsent,
    onStartRecordingSuccess,
    patientNameForConsentRecord,
    appointmentStartsAt,
    username,
    patient?.dob,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentOrganizationId,
    currentPracticeId,
    uploadDeps,
  ]);

  /**
   * Handle recording start for provider-only recording (NOT_REQUIRED consent status)
   */
  const handleStartProviderOnlyRecording = useCallback(async () => {
    try {
      const consentRequest: MarkConsentRequest = {
        consentStatus: PatientConsentStatus.NOT_REQUIRED,
        isGivenByPatient: true,
        patientFullName: patientNameForConsentRecord,
        patientAppointmentAt: appointmentStartsAt ?? '',
        recordedBy: username,
        patientDob: patient?.dob,
      };

      const response = await createPatientConsentRecord({
        consentRequest,
        unifiedAppointmentId: safeUnifiedAppointmentId(unifiedAppointmentId),
        standalonePatientVisitId: standalonePatientVisitId,
        currentOrganizationId,
        currentPracticeId,
        deps: uploadDeps,
      });
      const consentId = response.id;
      setPatientConsentRecordId(consentId);
      // Pass the consent record ID directly to avoid React state timing issues
      onStartProviderOnlyRecordingSuccess?.(consentId);
    } catch (error) {
      console.error(error);
    }
  }, [
    patientNameForConsentRecord,
    appointmentStartsAt,
    username,
    patient?.dob,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentOrganizationId,
    currentPracticeId,
    uploadDeps,
    onStartProviderOnlyRecordingSuccess,
  ]);

  /**
   * Handle when patient explicitly denies consent (NO)
   */
  const handlePatientConsentDenied = useCallback(async () => {
    try {
      const consentRequest: MarkConsentRequest = {
        consentStatus: PatientConsentStatus.NO,
        isGivenByPatient: true,
        patientFullName: patientNameForConsentRecord,
        patientAppointmentAt: appointmentStartsAt ?? '',
        recordedBy: username,
        patientDob: patient?.dob,
      };

      await createPatientConsentRecord({
        consentRequest,
        unifiedAppointmentId: safeUnifiedAppointmentId(unifiedAppointmentId),
        standalonePatientVisitId: standalonePatientVisitId,
        currentOrganizationId,
        currentPracticeId,
        deps: uploadDeps,
      });
      onPatientConsentDeniedSuccess?.();
    } catch (error) {
      console.error(error);
    }
  }, [
    patientNameForConsentRecord,
    appointmentStartsAt,
    username,
    patient?.dob,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentOrganizationId,
    currentPracticeId,
    uploadDeps,
    onPatientConsentDeniedSuccess,
  ]);

  // Reset consent record ID when appointment changes to prevent stale consent
  // from a previous patient being associated with a new patient's audio upload
  useEffect(() => {
    setPatientConsentRecordId(undefined);
  }, [unifiedAppointmentId, standalonePatientVisitId]);

  return {
    patientConsentRecordId,
    handleStartRecording,
    handleStartProviderOnlyRecording,
    handlePatientConsentDenied,
  };
};
